import { useState, useEffect, useMemo } from 'react';
import { CalendarEvent, ScheduleRule, WEEKDAY_NAMES } from '../types';
import { convertRulesToCalendarEvents } from '../lib/calendarHelpers';

type DNSPolicy = {
  id: string;
  name: string;
  schedule_type: string;
  is_active: boolean;
  start_time?: string;
  end_time?: string;
  days_of_week?: number[];
  block_social_media?: boolean;
  block_adult_content?: boolean;
  block_news?: boolean;
  block_gaming?: boolean;
  block_streaming?: boolean;
  created_at: string;
  updated_at?: string;
};

export function useCalendarEvents(policies: DNSPolicy[]) {
  const [events, setEvents] = useState<CalendarEvent[]>([]);

  // Convert DNS policies to ScheduleRule format
  const convertPoliciesToRules = (policies: DNSPolicy[]): ScheduleRule[] => {
    return policies
      .filter(policy => policy.schedule_type === 'scheduled' && policy.is_active)
      .map(policy => {
        const { category, color } = determineCategoryAndColor(policy);
        return {
          id: policy.id,
          site: policy.name,
          displayName: policy.name,
          startTime: policy.start_time || '00:00',
          endTime: policy.end_time || '23:59',
          days: policy.days_of_week?.map((day: number) => WEEKDAY_NAMES[day]).filter(Boolean) || [],
          isActive: policy.is_active,
          category,
          color,
          createdAt: policy.created_at,
          updatedAt: policy.updated_at || policy.created_at
        };
      });
  };

  // Determine category and color based on policy blocking settings
  const determineCategoryAndColor = (policy: DNSPolicy) => {
    // Social Media - Cyan
    if (policy.block_social_media) return { category: 'social', color: '#06B6D4' };
    // Adult Content - Red
    if (policy.block_adult_content) return { category: 'entertainment', color: '#DC2626' };
    // News - Blue
    if (policy.block_news) return { category: 'news', color: '#2563EB' };
    // Gaming - Purple
    if (policy.block_gaming) return { category: 'entertainment', color: '#7C3AED' };
    // Streaming - Orange
    if (policy.block_streaming) return { category: 'entertainment', color: '#EA580C' };
    // Custom/Mixed - Green
    return { category: 'custom', color: '#00F5A0' };
  };

  // Memoize the events calculation to prevent unnecessary recalculations
  const calculatedEvents = useMemo(() => {
    if (!policies || policies.length === 0) return [];
    
    const rules = convertPoliciesToRules(policies);
    if (rules.length === 0) return [];
    
    try {
      // Convert schedule rules to calendar events for 3 months ahead
      const ruleEvents = convertRulesToCalendarEvents(rules, 90);
      
      // Return only the rule-based events from actual DNS policies
      const allEvents = ruleEvents;
      return allEvents.slice(0, 200); // Show more events for recurring policies
    } catch (error) {
      console.error('Error calculating calendar events:', error);
      return [];
    }
  }, [policies]);

  useEffect(() => {
    setEvents(calculatedEvents);
  }, [calculatedEvents]);

  return events;
}
